import Cookies from 'universal-cookie'
import { navigate } from 'gatsby'
import { store } from '@redux/store'
import { setOrder, setReviewInvalidFields, setDeclineModalInfo, showCouponInvalidModal } from '@redux/modules/checkout'
import { clearCart } from '@redux/modules/cart'
import { placeOrder as placeOrderAPI } from '@services/checkout'
import { removeLocalStorage } from '@helpers/storage'
import { getLocation } from '@services/location'
import {
  LEGAL_REQUIREMENT_NAMES,
  SEVEN_YEAR_WARRANTY_NAMES,
} from '@components/checkout/checkout-parts/review-section/constants'
import { getOrder } from './global'
import { getCart } from '../cart'
import { placeOrderAnalytics } from '../google-tag-manager'

const cookies = new Cookies()

/**
 * Use to set order.reviewInfo, which contains the booleans pertaining to the
 * checkboxes that display in the Review step of Checkout. Calling with no
 * arguments will reset to initial state (all false)
 * @param {boolean | object} info boolean if field is included, object if field is omitted
 * @param {string} field fieldname whose value is the be set to info
 */
export const setReviewInfo = (info, field) => {
  const order = getOrder()

  if (field) {
    store.dispatch(setOrder({ ...order, reviewInfo: { ...order.reviewInfo, [field]: info } }))
  } else if (info && typeof info === 'object') {
    store.dispatch(setOrder({ ...order, reviewInfo: { ...info } }))
  } else {
    // reset reviewInfo to initial values
    store.dispatch(
      setOrder({
        ...order,
        reviewInfo: {
          acceptTerms: false,
          acceptPickupTerms: false,
          acceptServiceContractTerms: false,
          acceptSevenYearTerms: false,
        },
      }),
    )
  }
}

/**
 * Checks if user has checked all boxes for accepting the legal terms required for the order
 * and sets the reviewInvalidFields property in redux state to an empty array if all terms
 * are accepted or to ['accept'] if one or more required terms have not been accepted
 * @returns {string[]}
 */
export const validateReviewStep = async () => {
  const order = getOrder()
  const invalidFields = []

  const requiredTerms = getCartsLegalRequirements(order)
  const hasAllRequiredTermsAccepted = Object.keys(requiredTerms)
    .map(term => order.reviewInfo[term])
    .every(accepted => accepted)
  if (!hasAllRequiredTermsAccepted) invalidFields.push('accept')

  store.dispatch(setReviewInvalidFields(invalidFields))
  return invalidFields
}

export const placeOrderReview = async authtoken => {
  const order = getOrder()
  const { orderId = '', paymentInfo = [] } = order ?? {}
  const cart = getCart()
  const TL_RTG = cookies.get('__Host-TL_RTG')
  const session = cookies.get('__Host-rtgp')
  const { deliveryMode } = store.getState().checkout

  try {
    const res = await placeOrderAPI(orderId, session, TL_RTG, authtoken)
    if (res) {
      if (res.thankYouId) {
        removeLocalStorage('coupon')
        removeLocalStorage('couponType')
      }

      placeOrderAnalytics(!!res?.thankYouId, order, deliveryMode)
      store.dispatch(setOrder({ ...order, orderCart: { ...cart } }))
      store.dispatch(clearCart())

      navigate(`/order/success/${orderId}`)
    }
  } catch (err) {
    const { message: errorMessage } = err?.response?.data?.error || err || {}
    let responseCode = ''
    if (errorMessage) {
      try {
        const errMsg = JSON.parse(errorMessage)
        responseCode = errMsg?.error?.responseCode ?? ''
      } catch (error) {
        responseCode = ''
      }
    }

    const msg = (typeof errorMessage === 'string' ? errorMessage : errorMessage.info).toLowerCase()

    if (msg.includes('thank you coupon')) {
      store.dispatch(showCouponInvalidModal())
      removeLocalStorage('coupon')
      removeLocalStorage('couponType')
    } else if (msg) {
      const payTypes = paymentInfo.map(p => p.paymentType)
      const declineModalInfo = {}

      if (msg.includes('does not have a delivery date')) {
        declineModalInfo.declineType = 'Delivery'
      } else if (payTypes.includes('CYBERV3') && responseCode) {
        declineModalInfo.declineType = 'credit card'
        declineModalInfo.responseCode = responseCode
      } else if (payTypes.includes('CYBERV4')) {
        declineModalInfo.declineType = 'credit card'
        if (typeof errorMessage === 'string') {
          // this is for handling a 400/500 type error returned from placeOrder for multi-tender payments
          declineModalInfo.responseCode = errorMessage
        } else {
          declineModalInfo.multiTender = {
            errorMessage,
            payments: paymentInfo.find(p => p.paymentType === 'CYBERV4')?.paymentProperties ?? [],
          }
        }
      } else if (payTypes.includes('PALV2') && msg.includes('paypal')) {
        declineModalInfo.declineType = 'Paypal'
      } else if (payTypes.includes('APP') && msg.includes('credit card payment decline')) {
        declineModalInfo.declineType = 'Apple Pay'
      } else if (payTypes.includes('FIN') && msg.includes('rge declined authorization')) {
        declineModalInfo.declineType = 'Rooms To Go Financing'
      } else if (payTypes.includes('DBUY')) {
        declineModalInfo.declineType = 'Rooms To Go Financing'
      } else if (payTypes.includes('ACIMA')) {
        declineModalInfo.declineType = 'Acima'
      } else {
        store.dispatch(setReviewInvalidFields(['order']))
        declineModalInfo.declineType = 'order'
        declineModalInfo.responseCode = responseCode
      }

      store.dispatch(setDeclineModalInfo({ declineModalOpen: true, ...declineModalInfo }))
      setReviewInfo()
    }
  }
}

export const getFormattedCurrentDate = () =>
  new Date()
    .toDateString()
    .split(' ')
    .slice(1)
    .join(',')

export const getFormattedDate = dateString =>
  new Date(dateString)
    .toDateString()
    .split(' ')
    .slice(1)
    .join(',')

/* Warranty helpers */
export const orderHasSomeWarranty = order =>
  order && order.lineItems && order.lineItems.some(item => item.warrantyEnabled)

/**
 * Returns an object whose keys are the list of legal terms the order requires
 * based on the items included in the order. Each term will have a corresponding
 * checkbox the user must check in the Review section of Checkout before an
 * order can be placed.
 * @param {object} order
 * @returns {object}
 */
export const getCartsLegalRequirements = order => {
  // TOC terms checkbox is always required
  const requiredCheckboxes = { [LEGAL_REQUIREMENT_NAMES.TOC]: 'TOC' }

  // Pickup terms checkbox is required if order is a pickup order
  if (order?.isPickup) {
    requiredCheckboxes[LEGAL_REQUIREMENT_NAMES.PICKUP] = 'Pickup'
  }

  if (orderHasSomeWarranty(order)) {
    const isSevenYearWarranty = planName => SEVEN_YEAR_WARRANTY_NAMES.includes(planName)

    // 3-year ESC terms checkbox is required if any items include warranty that is not 7-year
    const threeYearEscItem = order.lineItems.find(
      item =>
        item.warrantyEnabled && item?.warrantyPrice?.planName && !isSevenYearWarranty(item.warrantyPrice.planName),
    )
    if (threeYearEscItem) {
      requiredCheckboxes[LEGAL_REQUIREMENT_NAMES.ESC3YR] = threeYearEscItem.warrantyPrice?.provider ?? ''
    }

    // 7-year ESC terms checkbox is required if any items include 7-year warranty (usually only Adjustable Frames)
    const sevenYearEscItem = order.lineItems.find(
      item => item.warrantyEnabled && item?.warrantyPrice?.planName && isSevenYearWarranty(item.warrantyPrice.planName),
    )
    if (sevenYearEscItem) {
      requiredCheckboxes[LEGAL_REQUIREMENT_NAMES.ESC7YR] = sevenYearEscItem.warrantyPrice?.provider ?? ''
    }
  }
  return requiredCheckboxes
}

/**
 * Retrieves the required terms checks for order.
 * @param {object} order
 * @returns {object}
 */
export const getRequiredTerms = async order => {
  const newTerms = await getCartsLegalRequirements(order)
  return newTerms
}

/**
 * Creates the 'customer' and 'transaction' objects from order that are needed for launching the Acima iframe
 * @param {object} order
 * @returns {object}
 */
export const getAcimaInfo = order => {
  const { contact, lineItems, orderId, shippingAddress, tax, totalDeliveryCharge } = order
  const { email, firstName, lastName, phone } = contact
  const { address1, address2, city, state, zip } = shippingAddress
  const address = { street1: address1, street2: address2, city, state, zipCode: zip }

  const customer = { address, email, firstName, lastName, middleName: '', phone }

  const acimaLineItems = []
  lineItems.forEach(item => {
    acimaLineItems.push({
      productId: item.sku ?? '',
      productName: item.title ?? '',
      unitPrice: Math.round((item?.salePrice || item.unitPrice) * 100),
      quantity: item.quantity,
    })

    if (item.warrantyEnabled && item?.warrantyPrice) {
      const { price, provider, planName } = item.warrantyPrice
      acimaLineItems.push({
        productId: planName ?? '',
        productName: `${provider ?? ''} ESC`.trim(),
        unitPrice: Math.round(price * 100),
        quantity: item.quantity,
      })
    }
  })

  // Note - Multiplying prices by 100 and rounding since Acima requires prices in cents and as integers
  // e.g, if our calculation returns a delivery price of 49.9899999 then we must send to Acima as 4999

  const transaction = {
    merchantReferenceNumber: orderId,
    discounts: 0,
    shipping: Math.round(totalDeliveryCharge * 100),
    salesTax: Math.round(tax * 100),
    lineItems: acimaLineItems,
  }

  return { customer, transaction }
}

export const resetReviewInvalidFields = () => store.dispatch(setReviewInvalidFields([]))
